import { NPCS } from '../../content/npcs';
import { hashString } from '../../core/rng';

/**
 * Bonds (rapport-lite, research 02 §9.1 / §11.2): songs, emotes and gifts
 * raise an NPC's bond through the stages in `BOND_STAGES`; stages pay out
 * skill potions, silver, the NPC's card, gifts and a title.
 */

// ================================================================ emotes

export type EmoteId = 'wave' | 'cheer' | 'bow' | 'dance' | 'sit' | 'laugh' | 'point' | 'salute';

export interface EmoteDef {
  id: EmoteId;
  name: string;
  /** Clip preference chain (first clip the model has wins). */
  clips: string[];
  /** Keeps playing until the actor moves. */
  loop: boolean;
  /** "Aldric waves." */
  verb: string;
}

export const EMOTES: EmoteDef[] = [
  { id: 'wave', name: 'Wave', clips: ['Emote_Wave', 'Victory'], loop: false, verb: 'waves' },
  { id: 'cheer', name: 'Cheer', clips: ['Emote_Cheer', 'Victory'], loop: false, verb: 'cheers' },
  { id: 'bow', name: 'Bow', clips: ['Emote_Bow', 'Buff'], loop: false, verb: 'bows' },
  { id: 'dance', name: 'Dance', clips: ['Emote_Dance', 'Victory'], loop: true, verb: 'dances' },
  { id: 'sit', name: 'Sit', clips: ['Emote_Sit', 'Idle'], loop: true, verb: 'sits down' },
  { id: 'laugh', name: 'Laugh', clips: ['Emote_Laugh', 'Buff', 'Victory'], loop: false, verb: 'laughs' },
  { id: 'point', name: 'Point', clips: ['Emote_Point', 'Victory', 'Buff'], loop: false, verb: 'points' },
  { id: 'salute', name: 'Salute', clips: ['Emote_Salute', 'Victory', 'Buff'], loop: false, verb: 'salutes' },
];
export const EMOTE_BY_ID: Record<string, EmoteDef> = Object.fromEntries(EMOTES.map((e) => [e.id, e]));

// ================================================================ songs

export interface SongDef {
  id: string;
  name: string;
  mood: string;
}

export const SONGS: SongDef[] = [
  { id: 'hearthlight', name: 'Hearthlight Air', mood: 'Warm and homely' },
  { id: 'tidewalker', name: "Tidewalker's Reel", mood: 'A brisk sailor’s jig' },
  { id: 'lantern_hymn', name: 'Lantern Hymn', mood: 'Solemn and bright' },
];
export const SONG_BY_ID: Record<string, SongDef> = Object.fromEntries(SONGS.map((s) => [s.id, s]));

// ================================================================ limits & stages

export const SONGS_PER_DAY = 5;
export const EMOTES_PER_DAY = 5;
export const GIFTS_PER_DAY = 20;
/** Emotes from the wheel count toward a bond NPC this close (m). */
export const EMOTE_BOND_RANGE = 6;

export const STAGE_COLORS = ['#a99a5f', '#b8d86a', '#4fc2b0', '#6aa8ff', '#ff6a90'];

// ================================================================ NPC profiles

export type GiftPref = 'loved' | 'liked' | 'neutral' | 'disliked';
export const GIFT_MULT: Record<GiftPref, number> = { loved: 2, liked: 1.5, neutral: 1, disliked: 0.4 };

export interface BondProfile {
  npc: string;
  /** Where they live (zone id + display name). */
  zone: string;
  home: string;
  favoriteSong: string;
  likedEmote: EmoteId;
  dislikedEmote: EmoteId;
  loves: string[];
  likes: string[];
  dislikes: string[];
  /** Hint shown in the panel. */
  taste: string;
  /** Card granted at Friendly. */
  card: string | null;
  /** Gift granted at Trusted. */
  trusted: { id: string; count: number }[];
  /** Title granted at Devoted. */
  title: string;
  lines: { song: string; favorite: string; emote: string; liked: string; disliked: string; gift: string; loved: string; disdain: string };
}

export const PROFILES: Record<string, BondProfile> = {
  captain_brenna: {
    npc: 'captain_brenna', zone: 'emberwild', home: 'Emberwild Vale',
    favoriteSong: 'tidewalker', likedEmote: 'salute', dislikedEmote: 'dance',
    loves: ['grenade_flame', 'bomb_destruction'], likes: ['hide', 'ore_raw', 'potion_small', 'potion_major'], dislikes: ['herb', 'herb_rare'],
    taste: 'A soldier’s gifts: grenades, hides and honest iron. Flowers make her sneeze.',
    card: 'c_brenna', trusted: [{ id: 'potion_major', count: 5 }, { id: 'grenade_flame', count: 3 }], title: 'Shield of the Outpost',
    lines: {
      song: 'Brenna taps her boot along.', favorite: '“Ha! My old company marched to that one.”', emote: 'Brenna nods, all business.',
      liked: 'Brenna snaps a crisp salute back.', disliked: '“On duty, Shardbearer. Save it for the tavern.”',
      gift: '“Useful. Thank you.”', loved: '“Now THAT is a proper gift.”', disdain: 'Brenna sneezes. “…Thanks, I suppose.”',
    },
  },
  high_warden_aldara: {
    npc: 'high_warden_aldara', zone: 'havenreach', home: 'Havenreach',
    favoriteSong: 'lantern_hymn', likedEmote: 'bow', dislikedEmote: 'laugh',
    loves: ['relic_rare'], likes: ['relic_fragment', 'catalyst', 'fusion_alloy'], dislikes: ['fish', 'hide'],
    taste: 'Relics of the old Lantern-keepers move her more than any jewel.',
    card: 'c_aldara', trusted: [{ id: 'catalyst', count: 2 }], title: 'Confidant of the Warden',
    lines: {
      song: 'Aldara listens with her eyes closed.', favorite: '“The old hymn… I have not heard it sung so well in years.”', emote: 'Aldara inclines her head.',
      liked: 'Aldara returns the bow with grave courtesy.', disliked: '“Something amuses you, Shardbearer?”',
      gift: '“You are thoughtful.”', loved: '“A true relic. The keepers would be proud of you.”', disdain: 'Aldara holds it at arm’s length.',
    },
  },
  minstrel_lio: {
    npc: 'minstrel_lio', zone: 'havenreach', home: 'Havenreach',
    favoriteSong: 'hearthlight', likedEmote: 'dance', dislikedEmote: 'salute',
    loves: ['herb_rare'], likes: ['timber_rare', 'fish', 'campfire_kit'], dislikes: ['ore_raw', 'bomb_destruction'],
    taste: 'Moonpetals for his hat, heartwood for his lutes, and a warm fire to play by.',
    card: 'c_lio', trusted: [{ id: 'potion_elixir', count: 2 }, { id: 'campfire_kit', count: 2 }], title: 'Muse of Havenreach',
    lines: {
      song: 'Lio harmonises on the second verse.', favorite: '“You play it like you were born by a hearth!”', emote: 'Lio grins.',
      liked: 'Lio whoops and joins the dance.', disliked: '“So stiff! Loosen those shoulders, friend.”',
      gift: '“How kind!”', loved: '“A Moonpetal! I shall write a song about this.”', disdain: '“What would a minstrel do with this?”',
    },
  },
  nomad_seer_zahra: {
    npc: 'nomad_seer_zahra', zone: 'sunscar', home: 'Sunscar Expanse',
    favoriteSong: 'tidewalker', likedEmote: 'point', dislikedEmote: 'cheer',
    loves: ['fish_rare'], likes: ['herb', 'relic_fragment', 'potion_panacea'], dislikes: ['timber', 'timber_rare'],
    taste: 'Water-songs and things that glow — the desert has too little of either.',
    card: 'c_zahra', trusted: [{ id: 'relic_rare', count: 1 }, { id: 'herb_rare', count: 3 }], title: 'Chosen of the Dunes',
    lines: {
      song: 'Zahra sways like a reed in the wind.', favorite: '“A song of the sea… here, of all places.”', emote: 'Zahra studies you quietly.',
      liked: 'Zahra follows your finger to the horizon and smiles.', disliked: '“Loud. The sand hears everything.”',
      gift: '“The dunes remember kindness.”', loved: '“It glows like the tide under a new moon.”', disdain: '“Wood rots out here. Keep it.”',
    },
  },
  abbess_vira: {
    npc: 'abbess_vira', zone: 'frosthollow', home: 'Frosthollow',
    favoriteSong: 'lantern_hymn', likedEmote: 'bow', dislikedEmote: 'dance',
    loves: ['potion_elixir', 'herb_rare'], likes: ['herb', 'potion_small', 'potion_major', 'campfire_kit'], dislikes: ['grenade_flame', 'grenade_dark', 'bomb_destruction'],
    taste: 'Remedies for the infirmary; never weapons within the abbey walls.',
    card: 'c_vira', trusted: [{ id: 'potion_elixir', count: 3 }], title: 'Beloved of the Abbey',
    lines: {
      song: 'Vira hums along softly.', favorite: '“The Lantern Hymn… it warms these cold halls.”', emote: 'Vira smiles kindly.',
      liked: 'Vira bows in return, hands folded.', disliked: '“Please. This is a house of mourning.”',
      gift: '“Bless you.”', loved: '“This will save lives in the infirmary.”', disdain: '“Not within these walls, child.”',
    },
  },
};

/** NPCs players can bond with (`bond: true`), in content order. */
export function bondNpcs(): string[] {
  return Object.values(NPCS)
    .filter((n) => n.bond)
    .map((n) => n.id);
}

/** Profile for a bond NPC; NPCs without one get neutral tastes derived from their id. */
export function profileFor(npc: string): BondProfile {
  const p = PROFILES[npc];
  if (p) return p;
  const h = hashString(npc);
  const name = NPCS[npc]?.name ?? npc;
  const liked = h % EMOTES.length;
  const disliked = (liked + 1 + ((h >>> 3) % (EMOTES.length - 1))) % EMOTES.length;
  return {
    npc, zone: '', home: 'Somewhere in the isles',
    favoriteSong: (SONGS[h % SONGS.length] as SongDef).id, likedEmote: (EMOTES[liked] as EmoteDef).id, dislikedEmote: (EMOTES[disliked] as EmoteDef).id,
    loves: [], likes: [], dislikes: [], taste: `${name} appreciates any thoughtful gift.`,
    card: null, trusted: [{ id: 'potion_major', count: 3 }], title: `Friend of ${name}`,
    lines: { song: `${name} listens.`, favorite: `${name} lights up.`, emote: `${name} nods.`, liked: `${name} laughs warmly.`, disliked: `${name} frowns.`, gift: '“Thank you.”', loved: '“How wonderful!”', disdain: '“Oh… thanks.”' },
  };
}
